import {Injectable} from '@angular/core';
import { HttpClient } from '@angular/common/http';
import {RuleResults, RuleResultsResponse} from './plan-detail.model';
import {BehaviorSubject, catchError, map, Observable, of, tap} from 'rxjs';
import {AppConfigService} from '../common/service/app-config.service';
import {PlanModelService} from '../common/service/plan-model.service';
import {NotificationAlertsService} from '../common/notification-alerts/notification-alerts.service';
import {PLAN_VALIDATION_STAGE, RULE_OUTCOME, TABS} from '../common/constants';
import {NotificationAlertsMessage} from '../common/notification-alerts/notification-alerts-message.model';
import {PlanLodgementService} from '../common/service/plan-lodgement.service';
import {NgEventBus} from 'ng-event-bus';

@Injectable({
    providedIn: 'root'
})
export class NlrPlanDetailService {
    title = 'Plan Detail Service';

    selectedTab: string;

    private validationResults = new BehaviorSubject<RuleResults[]>([]);

    private readonly stageNotificationAlertMessageMap: Map<string, Partial<NotificationAlertsMessage>> = new Map([
        [PLAN_VALIDATION_STAGE.LODGEMENT_INFORMATION, {subType: 'InformationRules', text: 'The Plan Information page has business rule errors!'}],
        [PLAN_VALIDATION_STAGE.LODGEMENT_DESIGN, {subType: 'DesignRules', text: 'The Design page has business rule errors!'}],
        [PLAN_VALIDATION_STAGE.LODGEMENT_ANALYSIS, {subType: 'AnalysisRules', text: 'The Analysis page has business rule errors!'}],
        [PLAN_VALIDATION_STAGE.LODGEMENT_INTERESTS, {subType: 'InterestRules', text: 'The Interests page has business rule errors!'}],
        [PLAN_VALIDATION_STAGE.LODGEMENT_CERTIFICATION, {subType: 'CertificationRules', text: 'The Certification page has business rule errors!'}],
        [PLAN_VALIDATION_STAGE.LODGEMENT_LODGE, {subType: 'LodgeRules', text: 'The Lodge page has business rule errors!'}]
    ]);

    constructor(
        private http: HttpClient,
        private appConfigService: AppConfigService,
        private planModelService: PlanModelService,
        private planLodgementService: PlanLodgementService,
        private notificationAlertsService: NotificationAlertsService,
        private eventBus: NgEventBus
    ) {}

    validationLoad(guid: string): Observable<RuleResults[]> {
        const url = `${this.appConfigService.settings.apiGatewayUrl}/validation/load/${guid}`;
        return this.http.get<{ruleResults: RuleResults[]}>(url).pipe(
            map((res: {ruleResults: RuleResults[]}) => {
                this.validationResults.next(res.ruleResults);
                return res.ruleResults;
            })
        );
    }

    runValidationsForStage(stage: string): Observable<RuleResultsResponse> {
        console.debug(`${this.title} - runValidationsForStage`);

        this.eventBus.cast('nlr:selectValidationTab', stage);

        return this.http
            .post(`${this.appConfigService.settings.apiGatewayUrl}/validation/run`, {
                planLodgementId: this.planModelService.getModel().id,
                stage: stage
            })
            .pipe(
                tap((data: RuleResultsResponse) => {
                    console.debug(`${this.title} - runValidationsForStage success!`);

                    const ruleResults = data.ruleResults;

                    // Merge stage results into all stage results array
                    const validationResults = this.validationResults.getValue();
                    validationResults.splice(
                        0,
                        validationResults.length,
                        ...validationResults.filter(validationResult => validationResult.stage !== stage),
                        ...ruleResults
                    );
                    this.validationResults.next(validationResults);

                    // Show stage results that are of type ERROR or CRITICAL
                    if (ruleResults.some(ruleResult => [RULE_OUTCOME.ERROR, RULE_OUTCOME.CRITICAL].includes(ruleResult.type))) {
                        this.notificationAlertsService.addMessage(this.stageNotificationAlertMessageMap.get(stage));
                    }
                }),
                catchError(error => {
                    const errorMessage = error.status === 422 ? `Error running validations: ${error.error}` : 'Error running validations';
                    console.error(errorMessage);
                    this.notificationAlertsService.addMessage({
                        subType: stage,
                        text: errorMessage
                    });
                    return of({ruleResults: []});
                })
            );
    }

    getValidationResults() {
        return this.validationResults;
    }

    hasValidationErrors() {
        return this.validationResults.getValue().some((ruleResults: RuleResults) => {
            if (ruleResults.type !== RULE_OUTCOME.ERROR && ruleResults.type !== RULE_OUTCOME.CRITICAL) {
                return false;
            }
            switch (ruleResults.stage) {
                case 'LODGEMENT_ANALYSIS':
                    return this.planLodgementService.analysisApplicable();
                case 'LODGEMENT_CERTIFICATION':
                    return this.planLodgementService.certificationApplicable();
                case 'LODGEMENT_DESIGN':
                    return this.planLodgementService.designApplicable();
                case 'LODGEMENT_INTERESTS':
                    return this.planLodgementService.interestsApplicable();
                default:
                    return true;
            }
        });
    }

    hasValidationErrorsExcludingLodgeTab() {
        return this.validationResults.getValue().some((ruleResults: RuleResults) => {
            if (ruleResults.type !== RULE_OUTCOME.ERROR && ruleResults.type !== RULE_OUTCOME.CRITICAL) {
                return false;
            }
            switch (ruleResults.stage) {
                case 'LODGEMENT_ANALYSIS':
                    return this.planLodgementService.analysisApplicable();
                case 'LODGEMENT_CERTIFICATION':
                    return this.planLodgementService.certificationApplicable();
                case 'LODGEMENT_DESIGN':
                    return this.planLodgementService.designApplicable();
                case 'LODGEMENT_INTERESTS':
                    return this.planLodgementService.interestsApplicable();
                case 'LODGEMENT_LODGE':
                    return false;
                default:
                    return true;
            }
        });
    }

    resetValidationResults() {
        this.validationResults.next([]);
    }

    isDesignTabSelected() {
        return this.selectedTab === TABS.DESIGN.toLowerCase();
    }
}
